import { onBeforeUnmount, ref, watch } from 'vue';
import { ownChannelGeneration } from 'dashboard/store/modules/internalChatMessageHelpers';

const intentKey = payload =>
  JSON.stringify({
    content: payload.content,
    contentFormat: payload.contentFormat,
    mentionedUserIds: payload.mentionedUserIds || [],
    mentionedTeamIds: payload.mentionedTeamIds || [],
    mentionAll: Boolean(payload.mentionAll),
    alsoSendInChannel: Boolean(payload.alsoSendInChannel),
  });

export const useInternalChatSend = ({
  store,
  accountId,
  channelId,
  threadState,
  conversation,
  threadPanel,
  beforeSend,
}) => {
  const isSending = ref(false);
  const failure = ref(null);
  const attempts = new Map();
  watch([accountId, () => store.state.internalChat.contextGeneration], () => {
    attempts.clear();
    failure.value = null;
  });
  onBeforeUnmount(() => attempts.clear());

  const send = async (payload, parentId = null) => {
    if (!channelId.value || isSending.value) return;
    const scope = {
      accountId: accountId.value,
      generation: store.state.internalChat.contextGeneration,
      channelId: channelId.value,
      parentId,
    };
    const ownsChannel = ownChannelGeneration(
      store.state.internalChat,
      scope.channelId
    );
    const isCurrent = () =>
      accountId.value === scope.accountId &&
      store.state.internalChat.contextGeneration === scope.generation &&
      ownsChannel();
    const panel = parentId ? threadPanel.value : conversation.value;
    const generation = threadState.value.generation;
    const key = `${scope.accountId}:${scope.channelId}:${parentId || 'root'}`;
    const signature = intentKey(payload);
    let attempt = attempts.get(key);
    if (
      !attempt ||
      attempt.signature !== signature ||
      attempt.files.length !== payload.attachments.length ||
      !attempt.files.every((file, index) => file === payload.attachments[index])
    ) {
      attempt = {
        signature,
        files: [...payload.attachments],
        echoId: crypto.randomUUID(),
        received: false,
      };
      attempts.set(key, attempt);
    }
    const snapshot = { ...payload, attachments: [...payload.attachments] };
    isSending.value = true;
    failure.value = null;
    try {
      await beforeSend();
      if (!isCurrent()) return;
      if (!attempt.received) {
        await store.dispatch('internalChat/sendMessage', {
          ...snapshot,
          channelId: scope.channelId,
          parentId,
          echoId: attempt.echoId,
        });
        attempt.received = true;
      }
      if (
        !isCurrent() ||
        !store.getters['internalChat/getChannels'].some(
          channel => channel.id === scope.channelId
        )
      )
        return;
      await store.dispatch('internalChat/deleteDraft', {
        channelId: scope.channelId,
        parentId,
      });
      attempts.delete(key);
      if (
        isCurrent() &&
        channelId.value === scope.channelId &&
        (!parentId || threadState.value.generation === generation)
      )
        panel?.resetComposer(snapshot);
    } catch (error) {
      if (isCurrent()) failure.value = { ...scope, error };
    } finally {
      if (!isCurrent()) attempts.delete(key);
      isSending.value = false;
    }
  };
  const failed = parentId =>
    failure.value &&
    failure.value.accountId === accountId.value &&
    failure.value.channelId === channelId.value &&
    failure.value.parentId === parentId;
  return { isSending, failed, send };
};
